<template>
  <li class="flex flex-col gap-3 rounded-md border bg-muted/20 p-3">
    <div v-if="!isEditing" class="flex min-w-0 items-center gap-2">
      <input
        type="checkbox"
        class="h-4 w-4 shrink-0 rounded border-border"
        :checked="isSelected"
        :disabled="isBusy"
        aria-label="Select checklist template item"
        @change="emit('toggle-selected', itemTemplate)"
      >

      <Button
        type="button"
        size="icon-sm"
        variant="outline"
        class="checklist-template-item-drag-handle h-6 w-6 shrink-0 cursor-grab p-0 text-muted-foreground shadow-none hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed"
        :disabled="isBusy"
        aria-label="Drag to reorder checklist template item"
      >
        <GripVertical class="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        class="h-9 min-w-0 max-w-full flex-1 justify-start overflow-hidden border border-transparent px-3 hover:border-border hover:bg-background/60"
        :disabled="isBusy"
        @click="startEdit"
      >
        <span class="block truncate font-medium text-foreground">
          {{ instructionLabel }}
        </span>
      </Button>

      <Button
        size="icon"
        variant="outline"
        class="shrink-0"
        :title="isDuplicating ? 'Duplicating checklist template item' : 'Duplicate checklist template item'"
        :loading="isDuplicating"
        :disabled="isBusy"
        @click="emit('duplicate', itemTemplate)"
      >
        <Copy class="h-4 w-4" />
        <span class="sr-only">Duplicate checklist template item</span>
      </Button>

      <Button
        size="icon"
        variant="outline"
        class="shrink-0 text-destructive hover:text-destructive"
        :title="isDeleting ? 'Deleting checklist template item' : 'Delete checklist template item'"
        :disabled="isBusy"
        @click="emit('delete', itemTemplate)"
      >
        <Trash2 class="h-4 w-4" />
        <span class="sr-only">Delete checklist template item</span>
      </Button>
    </div>

    <div v-else class="flex min-w-0 items-center gap-2">
      <input
        type="checkbox"
        class="h-4 w-4 shrink-0 rounded border-border"
        :checked="isSelected"
        :disabled="isBusy"
        aria-label="Select checklist template item"
        @change="emit('toggle-selected', itemTemplate)"
      >

      <Button
        type="button"
        size="icon-sm"
        variant="outline"
        class="checklist-template-item-drag-handle h-6 w-6 shrink-0 cursor-grab p-0 text-muted-foreground shadow-none hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed"
        :disabled="isBusy"
        aria-label="Drag to reorder checklist template item"
      >
        <GripVertical class="h-4 w-4" />
      </Button>

      <Input
        :id="instructionInputId"
        v-model="editableInstruction"
        class="min-w-0 max-w-full flex-1"
        placeholder="Describe the required action..."
        :disabled="isBusy"
        @keydown.enter.prevent="saveChecklistTemplateItem"
        @keydown.esc.prevent="cancelEdit"
      />

      <Button
        size="sm"
        class="shrink-0"
        :loading="isSaving"
        :disabled="!canSave || isBusy"
        @click="saveChecklistTemplateItem"
      >
        Save
      </Button>

      <Button
        size="sm"
        variant="outline"
        class="shrink-0"
        :disabled="isBusy"
        @click="cancelEdit"
      >
        Cancel
      </Button>

      <Button
        size="icon"
        variant="outline"
        class="shrink-0"
        :title="isDuplicating ? 'Duplicating checklist template item' : 'Duplicate checklist template item'"
        :loading="isDuplicating"
        :disabled="isBusy"
        @click="emit('duplicate', itemTemplate)"
      >
        <Copy class="h-4 w-4" />
        <span class="sr-only">Duplicate checklist template item</span>
      </Button>

      <Button
        size="icon"
        variant="outline"
        class="shrink-0 text-destructive hover:text-destructive"
        :title="isDeleting ? 'Deleting checklist template item' : 'Delete checklist template item'"
        :disabled="isBusy"
        @click="emit('delete', itemTemplate)"
      >
        <Trash2 class="h-4 w-4" />
        <span class="sr-only">Delete checklist template item</span>
      </Button>
    </div>

    <p v-if="!isEditing && hasDescription" class="whitespace-pre-wrap pl-8 text-xs text-muted-foreground">
      {{ normalizedCurrentDetails }}
    </p>

    <div v-if="isEditing" class="flex items-start justify-between gap-3">
      <Button
        type="button"
        variant="link"
        size="sm"
        class="h-auto w-fit px-0 text-xs underline underline-offset-2 hover:text-primary/80"
        :disabled="isBusy"
        @click="toggleDescription"
      >
        {{ descriptionActionLabel }}
      </Button>
    </div>

    <div v-if="isEditing && isDescriptionVisible" class="flex flex-col gap-1.5">
      <label class="text-xs font-medium text-muted-foreground">Description</label>
      <textarea
        v-model="editableDetails"
        rows="3"
        class="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none transition focus-visible:ring-1 focus-visible:ring-ring"
        placeholder="Add optional details for operators..."
        :disabled="isBusy"
      />
    </div>

    <p v-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>
  </li>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { Copy, GripVertical, Trash2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button/index.js'
import { Input } from '@/components/ui/input/index.js'

const props = defineProps({
  itemTemplate: {
    type: Object,
    required: true,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isDuplicating: {
    type: Boolean,
    default: false,
  },
  isSorting: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['save', 'delete', 'duplicate', 'toggle-selected'])

const editableInstruction = ref('')
const editableDetails = ref('')
const isDescriptionVisible = ref(false)
const isEditing = ref(false)
const hasPendingSave = ref(false)
const instructionInputId = computed(() => `checklist-template-item-instruction-${props.itemTemplate.id}`)

const normalizedCurrentInstruction = computed(() => {
  return String(props.itemTemplate.instruction ?? '').trim()
})

const normalizedCurrentDetails = computed(() => {
  return String(props.itemTemplate.details ?? '').trim()
})

const normalizedEditableInstruction = computed(() => {
  return String(editableInstruction.value ?? '').trim()
})

const normalizedEditableDetails = computed(() => {
  return String(editableDetails.value ?? '').trim()
})

const hasDescription = computed(() => {
  return normalizedCurrentDetails.value.length > 0
})

const instructionLabel = computed(() => {
  if (normalizedCurrentInstruction.value.length > 0) {
    return normalizedCurrentInstruction.value
  }

  return `Checklist template item #${props.itemTemplate.id}`
})

const descriptionActionLabel = computed(() => {
  if (isDescriptionVisible.value) {
    return 'Hide description'
  }

  return hasDescription.value ? 'Edit description' : 'Add a description'
})

const isBusy = computed(() => {
  return props.isSaving || props.isDeleting || props.isDuplicating || props.isSorting
})

const canSave = computed(() => {
  if (normalizedEditableInstruction.value.length === 0) {
    return false
  }

  return normalizedEditableInstruction.value !== normalizedCurrentInstruction.value
    || normalizedEditableDetails.value !== normalizedCurrentDetails.value
})

const synchronizeFormState = () => {
  editableInstruction.value = props.itemTemplate.instruction ?? ''
  editableDetails.value = props.itemTemplate.details ?? ''
}

const toggleDescription = () => {
  isDescriptionVisible.value = !isDescriptionVisible.value
}

const focusInstructionInput = async () => {
  await nextTick()

  const nativeInput = document.getElementById(instructionInputId.value)
  if (!nativeInput) {
    return
  }

  nativeInput.focus()
}

const startEdit = async () => {
  if (isBusy.value) {
    return
  }

  isEditing.value = true
  isDescriptionVisible.value = hasDescription.value
  await focusInstructionInput()
}

const cancelEdit = () => {
  if (isBusy.value) {
    return
  }

  synchronizeFormState()
  isDescriptionVisible.value = false
  isEditing.value = false
}

const saveChecklistTemplateItem = () => {
  if (!canSave.value || isBusy.value) {
    return
  }

  hasPendingSave.value = true

  emit('save', {
    itemTemplate: props.itemTemplate,
    instruction: editableInstruction.value,
    details: editableDetails.value,
  })
}

watch(() => props.itemTemplate, () => {
  synchronizeFormState()
}, { immediate: true, deep: true })

watch(() => props.errorMessage, (errorMessage) => {
  if (errorMessage) {
    hasPendingSave.value = false
    isEditing.value = true
    isDescriptionVisible.value = true
  }
})

watch(() => props.isSaving, (isSaving, wasSaving) => {
  if (!wasSaving || isSaving || !hasPendingSave.value) {
    return
  }

  hasPendingSave.value = false

  if (props.errorMessage) {
    return
  }

  isDescriptionVisible.value = false
  isEditing.value = false
})
</script>
